import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { categories, articles } from '@/mocks/data';

function CategorySchema({ cat }: { cat: typeof categories[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${cat.name} - 男人团`,
    description: cat.description,
    url: `https://nantuan.com/category/${cat.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '男人团',
      url: 'https://nantuan.com',
    },
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  if (!currentCategory) {
    return (
      <PageLayout breadcrumb={[{ label: '首页', path: '/' }, { label: '分类未找到' }]}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
      </PageLayout>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };

  const catColor = colorMap[currentCategory.color] || colorMap.primary;
  const otherCategories = categories.filter((c) => c.slug !== slug);

  return (
    <PageLayout
      breadcrumb={[
        { label: '首页', path: '/' },
        { label: currentCategory.name },
      ]}
    >
      <CategorySchema cat={currentCategory} />

      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${catColor}`}>
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-foreground-500 text-base">
                {currentCategory.description} · 共 {categoryArticles.length} 篇文章
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            <span className="text-foreground-400 text-sm mr-1">分类切换：</span>
            {otherCategories.map((cat) => {
              const c = colorMap[cat.color] || colorMap.primary;
              return (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200`}
                >
                  {cat.name}
                </Link>
              );
            })}
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-400">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`男人团 - ${article.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}